<template>
  <ib-application setupSSO setViewportMeta>
    <ProductDetails />
  </ib-application>
</template>

<script setup lang="ts">
import { inject, computed, watch, watchEffect, onBeforeUnmount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserState } from '@/stores/useUserState';
import { usePositionsManager } from '@/composables/usePositionsManager';
import { useCommonNativeActions } from '@/composables/useCommonNativeActions';
import { isOrderTicketPending } from '@/electionHub/composables/useOrderTicket';
import ProductDetails from '@/components/ProductDetails.vue';

// Module-shell setup for the case where the host app mounts ProductDetailsModule
// directly via the deep-link entry point. AppShell already does the equivalent
// when the user lands on '/' first and navigates internally — in that path we
// only render ProductDetails, not this wrapper. The product-details-specific
// bootstrap (productDetails store) lives in ProductDetails.vue so it fires
// consistently across both entry paths.
const route = useRoute();
const router = useRouter();

const moduleProps = inject<{
  underlyingConid: number;
  authenticated?: boolean;
  accountId?: string;
}>('moduleProps', {
  underlyingConid: 0,
  authenticated: false,
  accountId: '',
});
const userStateStore = useUserState();

const resolvedAccount = computed(() => moduleProps.accountId || (route.query.account as string | undefined) || '');
const resolvedAuthenticated = computed(() => (moduleProps.authenticated ?? false) || !!route.query.account);

watchEffect(() => {
  userStateStore.setUserState(resolvedAuthenticated.value, resolvedAccount.value);
});

usePositionsManager(resolvedAccount);

let popstateInProgress = false;

const onPopstate = () => {
  popstateInProgress = true;
  requestAnimationFrame(() => {
    popstateInProgress = false;
  });
};

const onHashChange = () => {
  if (popstateInProgress) return;
  if (isOrderTicketPending()) return;
  const hashPath = window.location.hash.slice(1) || '/';
  if (hashPath !== route.fullPath) {
    router.replace(hashPath);
  }
};

// Host app navigates via pushState which doesn't fire popstate/hashchange.
// Sync our router when the host signals a new product through moduleProps.
watch(() => moduleProps.underlyingConid, onHashChange);

onMounted(() => {
  window.addEventListener('popstate', onPopstate);
  window.addEventListener('hashchange', onHashChange);
});

useCommonNativeActions();

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopstate);
  window.removeEventListener('hashchange', onHashChange);
});
</script>
